import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {format} from 'date-fns';
import {Check, Loader2, Search} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@common-web/components/ui/dialog';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {toast} from '@common-web/hooks/use-toast';
import {trpc} from '@common-web/lib/trpc';
import {cn} from '@common-web/lib/utils';
import {TenantTag} from '@internal/pages/group-pipeline/components/TenantTag';

export interface StartPipelineGroup {
  vbaGroupId: string;
  name: string;
  tenantName: string | null;
}

// Puts an existing VBA group into the pipeline. Opened from the page header (pick a group)
// or from a group's own page (group already chosen).
export function StartPipelineDialog({
  open,
  onOpenChange,
  group,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  group?: StartPipelineGroup;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const utils = trpc.useUtils();
  const [picked, setPicked] = useState<StartPipelineGroup | null>(group ?? null);
  const [receivedOn, setReceivedOn] = useState(format(new Date(), 'yyyy-MM-dd'));

  const start = trpc.internal.groupPipeline.start.useMutation({
    onSuccess: async (_pipelineId, {vbaGroupId}) => {
      await Promise.all([
        utils.internal.groupPipeline.list.invalidate(),
        utils.internal.groupPipeline.board.invalidate(),
        utils.internal.groupPipeline.boardColumn.invalidate(),
        utils.internal.groupPipeline.detail.invalidate({vbaGroupId}),
        utils.internal.groupPipeline.activity.invalidate({vbaGroupId}),
      ]);
      toast({title: 'Added to the pipeline'});
      onOpenChange(false);
      // Keep wherever Back already pointed (the list or board), or come back here.
      const from =
        (location.state as {from?: string} | null)?.from ?? location.pathname + location.search;
      navigate(`/group-pipeline/${vbaGroupId}?tab=tasks`, {state: {from}});
    },
  });

  // Keyed on the ID so a parent re-render doesn't wipe what the user changed.
  const groupId = group?.vbaGroupId;
  useEffect(() => {
    if (open) {
      setPicked(group ?? null);
      setReceivedOn(format(new Date(), 'yyyy-MM-dd'));
      start.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, groupId]);

  const submit = () => {
    if (!picked || !receivedOn) return;
    // Noon local time keeps the picked day from shifting across time zones.
    start.mutate({vbaGroupId: picked.vbaGroupId, receivedAt: new Date(`${receivedOn}T12:00:00`)});
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='grid-cols-[minmax(0,1fr)] sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle>Add to pipeline</DialogTitle>
          <DialogDescription>
            Copies the tenant&apos;s task list onto this group and opens its first steps.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label>Group</Label>
            {group ? (
              <GroupSummary group={group} />
            ) : (
              <GroupPicker picked={picked} onPick={setPicked} />
            )}
          </div>

          <div className='space-y-2'>
            <Label htmlFor='start-pipeline-received'>RFC received on</Label>
            <Input
              id='start-pipeline-received'
              type='date'
              value={receivedOn}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => setReceivedOn(e.target.value)}
              className='w-48'
            />
            <p className='text-xs text-gray-500'>
              Due dates count from this day. Use the real date for groups already in progress.
            </p>
          </div>

          {start.error && (
            <p role='alert' className='rounded-md bg-red-50 px-3 py-2 text-sm text-red-700'>
              {start.error.message}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!picked || !receivedOn || start.isPending} onClick={submit}>
            {start.isPending && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
            Add to pipeline
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GroupSummary({group}: {group: StartPipelineGroup}) {
  return (
    <div className='flex items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2'>
      <div className='min-w-0'>
        <p className='truncate text-sm font-medium' title={group.name}>
          {group.name}
        </p>
        <p className='text-xs text-gray-500'>{group.vbaGroupId}</p>
      </div>
      <TenantTag shortName={group.tenantName} />
    </div>
  );
}

// Searches groups that can be started: in a tenant with a task list and not in the pipeline yet.
function GroupPicker({
  picked,
  onPick,
}: {
  picked: StartPipelineGroup | null;
  onPick: (group: StartPipelineGroup) => void;
}) {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearch(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const results = trpc.internal.groupPipeline.list.useQuery(
    {search: search || undefined, phases: ['Active'], page: 1, pageSize: 20},
    // Keep the last results up while the next search loads, so the list doesn't flash.
    {placeholderData: (previous) => previous},
  );
  const startable = results.data?.rows.filter((row) => !row.hasPipeline) ?? [];
  const searching = input.trim() !== search || results.isFetching;

  return (
    <div className='space-y-2'>
      <div className='relative'>
        <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500' />
        <Input
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder='Search name or ID'
          className='pl-9 pr-9'
        />
        {searching && (
          <Loader2 className='absolute inset-y-0 right-3 my-auto h-4 w-4 animate-spin text-gray-400' />
        )}
      </div>
      {/* Fixed height so the dialog doesn't resize as results change. */}
      <div className='h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white p-1 [scrollbar-gutter:stable]'>
        {!results.data ? (
          <PickerMessage>Loading groups…</PickerMessage>
        ) : startable.length === 0 ? (
          <PickerMessage>
            {search ? 'No groups to add match that search.' : 'No groups left to add.'}
          </PickerMessage>
        ) : (
          startable.map((row) => {
            const selected = picked?.vbaGroupId === row.vbaGroupId;
            return (
              <button
                key={row.vbaGroupId}
                type='button'
                onClick={() =>
                  onPick({vbaGroupId: row.vbaGroupId, name: row.name, tenantName: row.tenantName})
                }
                className={cn(
                  'flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-gray-100',
                  selected && 'bg-gray-100',
                )}
              >
                <div className='min-w-0 flex-1'>
                  <p className='truncate text-sm font-medium' title={row.name}>
                    {row.name}
                  </p>
                  <p className='text-xs text-gray-500'>{row.vbaGroupId}</p>
                </div>
                <TenantTag shortName={row.tenantName} />
                <Check className={cn('h-4 w-4 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

function PickerMessage({children}: {children: string}) {
  return (
    <p className='flex h-full items-center justify-center px-3 text-center text-sm text-gray-500'>
      {children}
    </p>
  );
}
